import {
  HHAlert,
  HHBox,
  HHButton,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { Skeleton } from '@mui/material';
import { useState } from 'react';
import { AttentionCard } from '../components/attention-card';
import { ChipFilter, ChipFilterOption } from '../components/chip-filter';
import { FadeIn, StaggerItem, StaggerList } from '../components/motion';
import { PageHeader } from '../components/page-header';
import { ProviderCard } from '../components/provider-card';
import {
  ProvidersTable,
  PROVIDERS_TABLE_NO_PROVIDERS_MESSAGE,
} from '../components/providers-table';
import { SurfaceCard } from '../components/surface-card';
import { ThroughputPanel } from '../components/throughput-panel';
import { VerdictLine } from '../components/verdict-line';
import { PROVIDER_HEALTH_LABELS } from '../constants/partner-data-gateway.constant';
import { usePermissions } from '../context/permissions-context';
import { ProviderSummary } from '../domain/types';
import { useIngestionSummary } from '../hooks/use-ingestion-summary';
import { useProviderSummaries } from '../hooks/use-provider-summaries';
import { HealthFilterValue, PROVIDER_HEALTH_VALUES } from '../types';
import { getProviderHealth } from '../utils/provider-health.utils';
import { buildVerdict } from '../utils/verdict.utils';

export const OVERVIEW_PAGE_TEST_ID = 'partner-data-gateway-overview-page';
export const OVERVIEW_PAGE_NO_ACCESS_TEST_ID =
  'partner-data-gateway-overview-page-no-access';
export const OVERVIEW_PAGE_TITLE = 'Partner Data Gateway';
export const OVERVIEW_NEEDS_A_LOOK_TEST_ID = 'pdg-overview-needs-a-look';
export const OVERVIEW_RECENT_TEST_ID = 'pdg-overview-recently-ingested';
export const OVERVIEW_ERROR_TEST_ID = 'pdg-overview-error';
export const OVERVIEW_RETRY_TEST_ID = 'pdg-overview-retry-button';
export const HEALTH_FILTER_TEST_ID_PREFIX = 'pdg-health-chip';
export const OVERVIEW_ERROR_MESSAGE = 'Something went wrong loading providers.';

const RECENT_CARD_COUNT = 8;
const SKELETON_CARD_COUNT = 4;

const HEALTH_FILTER_OPTIONS: ChipFilterOption<HealthFilterValue>[] = [
  { value: 'all', label: 'All' },
  ...PROVIDER_HEALTH_VALUES.map(health => ({
    value: health,
    label: PROVIDER_HEALTH_LABELS[health],
  })),
];

const byLatestDesc = (a: ProviderSummary, b: ProviderSummary): number =>
  (b.latestIngestion?.createdAt ?? '').localeCompare(
    a.latestIngestion?.createdAt ?? '',
  );

/** Failing providers first, then unconfigured; newest file first within each. */
const needsALook = (summaries: ProviderSummary[]): ProviderSummary[] => {
  const failing = summaries
    .filter(s => getProviderHealth(s) === 'failing')
    .sort(byLatestDesc);
  const unconfigured = summaries
    .filter(s => !s.hasConfiguration && getProviderHealth(s) !== 'failing')
    .sort(byLatestDesc);

  return [...failing, ...unconfigured];
};

/** The unfiltered fleet size, next to a footer that counts filtered rows. */
const describeTotal = (total: number): string =>
  `${total} ${total === 1 ? 'provider' : 'providers'} total`;

const recentlyIngested = (summaries: ProviderSummary[]): ProviderSummary[] =>
  summaries
    .filter(s => s.latestIngestion !== null)
    .sort(byLatestDesc)
    .slice(0, RECENT_CARD_COUNT);

const SectionHeading = ({
  children,
  aside,
}: {
  children: string;
  aside?: string;
}): JSX.Element => (
  <HHStack direction="row" justifyContent="space-between" alignItems="baseline">
    <HHTypography
      hhVariant="variant-bypass"
      variant="h6"
      component="h2"
      sx={{ fontSize: 16, fontWeight: 600 }}
    >
      {children}
    </HHTypography>
    {aside && (
      <HHTypography
        hhVariant="variant-bypass"
        variant="body2"
        color="text.secondary"
      >
        {aside}
      </HHTypography>
    )}
  </HHStack>
);

const cardGridSx = {
  display: 'grid',
  gap: 4,
  gridTemplateColumns: {
    xs: '1fr',
    sm: 'repeat(2, 1fr)',
    lg: 'repeat(4, 1fr)',
  },
} as const;

// HHStack's exposed prop types drop `component`, so page sections that need a
// semantic <section> element use HHBox with this flex-column shim instead.
const sectionStackSx = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3,
} as const;

const CardSkeletons = (): JSX.Element => (
  <HHBox sx={cardGridSx}>
    {Array.from({ length: SKELETON_CARD_COUNT }, (_, index) => (
      <SurfaceCard key={index} padding={5}>
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
        <Skeleton
          variant="rectangular"
          height={18}
          sx={{ marginTop: 2, borderRadius: 1 }}
        />
      </SurfaceCard>
    ))}
  </HHBox>
);

export const OverviewPage = (): JSX.Element => {
  const { canViewIngestions } = usePermissions();
  const { summaries, isLoading, hasError, refetch } = useProviderSummaries();
  const { stats } = useIngestionSummary();
  const [healthFilter, setHealthFilter] = useState<HealthFilterValue>('all');

  if (!canViewIngestions) {
    return (
      <HHAlert hhVariant="outlined" severity="warning">
        <span data-testid={OVERVIEW_PAGE_NO_ACCESS_TEST_ID}>
          You do not have access to view partner data ingestions.
        </span>
      </HHAlert>
    );
  }

  const verdict = buildVerdict(summaries);
  const attention = needsALook(summaries);
  const recent = recentlyIngested(summaries);
  const filtered =
    healthFilter === 'all'
      ? summaries
      : summaries.filter(s => getProviderHealth(s) === healthFilter);

  return (
    <HHStack
      flex={1}
      direction="column"
      gap={6}
      paddingX={6}
      paddingY={6}
      data-testid={OVERVIEW_PAGE_TEST_ID}
    >
      <PageHeader
        title={OVERVIEW_PAGE_TITLE}
        subtitle={<VerdictLine verdict={verdict} isLoading={isLoading} />}
      />

      <ThroughputPanel stats={stats} />

      {hasError && (
        <HHBox data-testid={OVERVIEW_ERROR_TEST_ID}>
          <HHAlert
            hhVariant="outlined"
            severity="error"
            action={
              <HHButton
                hhVariant="variant-bypass"
                variant="text"
                size="small"
                onClick={refetch}
                data-testid={OVERVIEW_RETRY_TEST_ID}
              >
                Retry
              </HHButton>
            }
          >
            {OVERVIEW_ERROR_MESSAGE}
          </HHAlert>
        </HHBox>
      )}

      {!hasError && isLoading && summaries.length === 0 && <CardSkeletons />}

      {!hasError && !isLoading && attention.length > 0 && (
        <FadeIn>
          <HHBox
            component="section"
            data-testid={OVERVIEW_NEEDS_A_LOOK_TEST_ID}
            sx={sectionStackSx}
          >
            <SectionHeading aside="Failing first, then most recent">
              Needs a look
            </SectionHeading>
            <StaggerList
              sx={{
                ...cardGridSx,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              }}
            >
              {attention.map(summary => (
                <StaggerItem key={summary.dataProvider}>
                  <AttentionCard summary={summary} />
                </StaggerItem>
              ))}
            </StaggerList>
          </HHBox>
        </FadeIn>
      )}

      {!hasError && !isLoading && recent.length > 0 && (
        <FadeIn delayMs={50}>
          <HHBox
            component="section"
            data-testid={OVERVIEW_RECENT_TEST_ID}
            sx={sectionStackSx}
          >
            <SectionHeading aside="Newest file first">
              Recently ingested
            </SectionHeading>
            <StaggerList sx={cardGridSx}>
              {recent.map(summary => (
                <StaggerItem key={summary.dataProvider}>
                  <ProviderCard summary={summary} />
                </StaggerItem>
              ))}
            </StaggerList>
          </HHBox>
        </FadeIn>
      )}

      {!hasError && (
        <HHBox component="section" sx={sectionStackSx}>
          <SectionHeading aside={describeTotal(summaries.length)}>
            All providers
          </SectionHeading>
          <ProvidersTable
            summaries={filtered}
            isLoading={isLoading}
            emptyMessage={
              healthFilter === 'all'
                ? PROVIDERS_TABLE_NO_PROVIDERS_MESSAGE
                : undefined
            }
            toolbar={
              <ChipFilter
                options={HEALTH_FILTER_OPTIONS}
                value={healthFilter}
                onChange={setHealthFilter}
                ariaLabel="Filter by health"
                testIdPrefix={HEALTH_FILTER_TEST_ID_PREFIX}
              />
            }
          />
        </HHBox>
      )}
    </HHStack>
  );
};

export default OverviewPage;
